import type { GridApi, RowClassParams } from "ag-grid-community";
import { observer } from "mobx-react-lite";
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import AddRequestLogToDatasetDialog from "./AddRequestLogToDatasetDialog";
import SelectionActionBar, {
  type RequestImportConfig,
  type SignalSampleConfig,
} from "./SelectionActionBar";
import type { RequestRow } from "./hooks/requestLogGridTypes";
import { useRequestColumns } from "./hooks/useRequestColumns";
import { getRequestLogRowId, useRequestLogsGridData } from "./hooks/useRequestLogsGridData";
import { useRequestLogsGridInteractions } from "./hooks/useRequestLogsGridInteractions";
import { useRequestLogsPagingPanel } from "./hooks/useRequestLogsPagingPanel";
import { useResetRequestLogsPageOnStructuredSearch } from "./hooks/useResetRequestLogsPageOnStructuredSearch";
import { useRequestLogsSelectionStore } from "./request-logs-selection-context";

import { StructuredSearchbar, useStructuredSearchStore } from "@/components/StructuredSearch";
import type { Filter } from "@/components/StructuredSearch/config/types";
import {
  PROMPT_ANALYTICS_URL_NAMESPACE,
  REQUESTS_TABLE_URL_NAMESPACE,
} from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { useStructuredSearchURLSync } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import { useSyncStructuredSearchConstraints } from "@/components/StructuredSearch/hooks/useSyncStructuredSearchConstraints";
import type { CompleteDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { useOptionalRequestLogsTableRefresh } from "@/components/RequestsAnalytics/request-logs-table-refresh-context";
import { StructuredSearchInfiniteGridShell } from "@/components/ui/ag-grid-table/StructuredSearchInfiniteGridShell";
import { ButtonSize } from "@/components/ui/button";
import { useTimezone } from "@/context/timezone-context";
import { useApplyOlderDataConstraints } from "@/routes/request-logs/search/hooks/useApplyOlderDataConstraints";
import { isWorkspacePromptVersionPathname } from "@/utils/workspacePathMatchers";

export interface RequestLogsAgGridProps {
  size?: ButtonSize;
  lockedFilters?: Filter[];
  initialDateRange?: CompleteDateRange;
  dateRangeMinDate?: Date;
  dateRangeMaxDate?: Date;
  syncUrl?: boolean;
  searchTitle?: string;
  showSearchbar?: boolean;
  useExternalStructuredSearchProvider?: boolean;
  trackingSurface?: "requests_analytics";
  searchbarContainerClassName?: string;
  searchbarHeaderActions?: ReactNode;
  onLoadingChange?: (isLoading: boolean) => void;
  /** Fires after add-to-table job is queued successfully (e.g. close a host dialog). */
  onAddToTableEnqueueSuccess?: () => void;
  requestImportConfig?: RequestImportConfig;
  signalSampleConfig?: SignalSampleConfig;
}

const EMPTY_LOCKED_FILTERS: Filter[] = [];

const RequestLogsGridContent = observer(
  ({
    size: _size,
    lockedFilters = EMPTY_LOCKED_FILTERS,
    initialDateRange,
    dateRangeMinDate,
    dateRangeMaxDate,
    syncUrl = true,
    searchTitle = "Filters",
    showSearchbar = true,
    useExternalStructuredSearchProvider = false,
    trackingSurface,
    searchbarContainerClassName = "py-4",
    searchbarHeaderActions,
    onLoadingChange,
    onAddToTableEnqueueSuccess,
    requestImportConfig,
    signalSampleConfig,
  }: RequestLogsAgGridProps) => {
    const tableRefresh = useOptionalRequestLogsTableRefresh();
    const structuredSearchStore = useStructuredSearchStore();
    const { pathname } = useLocation();
    const urlNamespace = isWorkspacePromptVersionPathname(pathname)
      ? PROMPT_ANALYTICS_URL_NAMESPACE
      : REQUESTS_TABLE_URL_NAMESPACE;
    useStructuredSearchURLSync(
      structuredSearchStore,
      syncUrl && structuredSearchStore.urlHydrationReady,
      urlNamespace,
    );
    useApplyOlderDataConstraints(!useExternalStructuredSearchProvider);
    const { timezone } = useTimezone();

    // External provider owns locked filters and date range (e.g. prompt overview).
    useSyncStructuredSearchConstraints({
      lockedFilters,
      dateRange: initialDateRange,
      syncLockedFilters: !useExternalStructuredSearchProvider,
      syncDateRange: !useExternalStructuredSearchProvider,
      store: structuredSearchStore,
    });

    const selection = useRequestLogsSelectionStore();
    const { gridContainerRef, pagingPanel } = useRequestLogsPagingPanel();
    const [datasetRequestLogId, setDatasetRequestLogId] = useState<number | null>(null);

    useResetRequestLogsPageOnStructuredSearch();

    const {
      contextMenuItems,
      currentPage,
      getRowHref,
      handlePaginationChanged,
      handleRowClick,
      handleRowLinkClick,
      handleSortChanged,
      pageSize,
      recentlyViewedRequestId,
    } = useRequestLogsGridInteractions(trackingSurface, row => {
      const requestLogId = Number(row.id);
      if (!Number.isFinite(requestLogId)) {
        return;
      }
      setDatasetRequestLogId(requestLogId);
    });

    const isPromptEntityAnalytics = isWorkspacePromptVersionPathname(pathname);
    const columns = useRequestColumns(timezone, contextMenuItems, {
      hidePromptName: isPromptEntityAnalytics,
      prioritizeStartTime: isPromptEntityAnalytics,
      selectionLimit: requestImportConfig?.selectionLimit,
    });
    const {
      datasource,
      handleManualRefresh,
      hasCompletedInitialLoad,
      hasNewData,
      isTableLoading,
      setGridApiRef,
      totalRows,
    } = useRequestLogsGridData(columns);

    const handleManualRefreshRef = useRef(handleManualRefresh);
    handleManualRefreshRef.current = handleManualRefresh;

    useLayoutEffect(() => {
      if (!tableRefresh || showSearchbar || !useExternalStructuredSearchProvider) {
        return;
      }
      const runLatestRefresh = () => {
        handleManualRefreshRef.current();
      };
      tableRefresh.registerHandler(runLatestRefresh);
      return () => {
        tableRefresh.registerHandler(null);
      };
    }, [showSearchbar, tableRefresh, useExternalStructuredSearchProvider]);

    useEffect(() => {
      onLoadingChange?.(isTableLoading);
    }, [isTableLoading, onLoadingChange]);

    useEffect(
      () => () => {
        onLoadingChange?.(false);
      },
      [onLoadingChange],
    );

    useEffect(() => {
      selection.clear();
    }, [
      selection,
      structuredSearchStore.q,
      structuredSearchStore.filters,
      structuredSearchStore.dateRange.from,
      structuredSearchStore.dateRange.to,
    ]);

    const gridApiRef = useRef<GridApi<RequestRow> | null>(null);
    const handleSetGridApiRef = useCallback(
      (api: GridApi<RequestRow>) => {
        if (!api) {
          return;
        }
        gridApiRef.current = api;
        setGridApiRef(api);
      },
      [setGridApiRef],
    );

    const selectedIdsKey = selection.selectedIds.join(",");
    useEffect(() => {
      gridApiRef.current?.redrawRows();
    }, [
      selection.mode,
      selection.count,
      selection.matchingLimit,
      selectedIdsKey,
      recentlyViewedRequestId,
    ]);

    const getRowClass = (params: RowClassParams<RequestRow>) => {
      if (params.node.detail) {
        return "!bg-white hover:!bg-white";
      }

      const rawId = params.data?.id;
      const numericId = typeof rawId === "number" ? rawId : Number(rawId);
      if (
        Number.isFinite(numericId) &&
        recentlyViewedRequestId &&
        String(numericId) === String(recentlyViewedRequestId)
      ) {
        return "!bg-slate-50";
      }

      if (selection.mode === "all_matching") {
        const limit = selection.matchingLimit ?? selection.count;
        return params.node.rowIndex !== null && params.node.rowIndex < limit ? "!bg-blue-50" : "";
      }

      return Number.isFinite(numericId) && selection.has(numericId) ? "!bg-blue-50" : "";
    };

    return (
      <div className="flex h-full w-full flex-col bg-white">
        {showSearchbar && (
          <div className="flex-shrink-0 overflow-visible">
            <div className={searchbarContainerClassName}>
              <StructuredSearchbar
                className="w-full"
                onRefresh={handleManualRefresh}
                isRefreshing={isTableLoading}
                hasNewData={hasNewData}
                title={searchTitle}
                minDate={dateRangeMinDate ?? initialDateRange?.from}
                maxDate={dateRangeMaxDate ?? initialDateRange?.to}
                headerActions={searchbarHeaderActions}
              />
            </div>
          </div>
        )}

        <StructuredSearchInfiniteGridShell<RequestRow>
          gridContainerRef={gridContainerRef}
          pagingPanel={pagingPanel}
          columns={columns}
          datasource={datasource}
          contextMenuItems={contextMenuItems}
          currentPage={currentPage}
          pageSize={pageSize}
          hasCompletedInitialLoad={hasCompletedInitialLoad}
          isTableLoading={isTableLoading}
          loadingLabel="Loading requests..."
          loadingDescription="We are fetching the matching request logs"
          emptyTitle="No requests found"
          emptyDescription="Try adjusting the current search filters"
          showEmptyState={hasCompletedInitialLoad && !isTableLoading && totalRows === 0}
          getRowClass={getRowClass}
          getRowId={params => getRequestLogRowId(params.data)}
          onRowClick={handleRowClick}
          getRowHref={getRowHref}
          onRowLinkClick={handleRowLinkClick}
          onSortChanged={handleSortChanged}
          onPaginationChanged={handlePaginationChanged}
          getApiRef={handleSetGridApiRef}
          pagingPanelContent={
            <div className="order-first mr-auto flex h-full items-center">
              <SelectionActionBar
                totalMatching={totalRows}
                onAddToTableEnqueueSuccess={onAddToTableEnqueueSuccess}
                requestImportConfig={requestImportConfig}
                signalSampleConfig={signalSampleConfig}
              />
            </div>
          }
        />
        <AddRequestLogToDatasetDialog
          isOpen={datasetRequestLogId !== null}
          onOpenChange={open => {
            if (!open) {
              setDatasetRequestLogId(null);
            }
          }}
          requestLogId={datasetRequestLogId}
        />
      </div>
    );
  },
);

export default RequestLogsGridContent;
